import { useNavigate } from "react-router-dom";
import Button from "../../components/buttons/Button";
import PageLayout from "../../layouts/PageLayout";
import { routes } from "../../utils/routes";
import { useEffect, useState, useCallback } from "react";
import { TClass } from "../../types/Types";
import apiRequest from "../../helpers/apiRequest";
import { Urls } from "../../utils/urls";
import Loading from "../../components/loading/Loading";
import GenereteLinkModal from "./components/GenereteLinkModal";
import Pagination from "../../components/pagination/Pagination";
import {
  MagnifyingGlassIcon,
  InboxIcon,
  ChevronDownIcon,
  LinkIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";

type TModals = {
  linkGenerate?: TClass;
};

const Classes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [modals, setModals] = useState<TModals>({});
  const [classes, setClasses] = useState<TClass[]>([]);

  // Pagination & Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const fetchClasses = useCallback(async (pNo = 1, search = searchQuery) => {
    setLoading(true);
    const { data, error } = await apiRequest<{
      classes: TClass[];
      totalPages: number;
      totalItems?: number;
    }>({
      method: "GET",
      url: `${Urls.all_classes}?pageNo=${pNo}&limit=${limit}&search=${search}`,
    });

    if (error) {
      alert(error);
    } else if (data) {
      setClasses(data.classes || []);
      setTotalPages(data.totalPages || 0);
      setTotalItems(data.totalItems || 0);
      setPageNo(pNo);
    }
    setLoading(false);
  }, [limit, searchQuery]);

  const deleteClass = async (id: string) => {
    const confirm = window.confirm("Are you sure want to delete?");
    if (!confirm) return;
    const { data, error } = await apiRequest<{ message: string }>({
      method: "DELETE",
      url: `${Urls.create_class}/${id}`,
    });
    if (error) {
      alert(error);
    } else if (data?.message) {
      fetchClasses(pageNo);
    }
  };

  // Handle Search Debounce
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchClasses(1, searchQuery); 
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery, limit]);

  // Initial fetch is handled by the search effect above since searchQuery defaults to ""

  if (loading && classes.length === 0) return <Loading />;

  return (
    <PageLayout
      label="Classes"
      leftComp={
        <Button
          title="Create Class"
          onClick={() => navigate(routes.add_classes)}
          className="flex items-center gap-2"
        />
      }
    >
      <div className="max-w-[1400px] mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 border-b border-gray-100 pb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Classes</h1>
            <p className="text-gray-500 text-sm mt-1">
              Showing {classes.length} classes
              {totalItems > 0 && ` of ${totalItems} total`}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* Limit Selector */}
            <div className="relative inline-block w-full sm:w-auto">
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPageNo(1);
                }}
                className="appearance-none w-full sm:w-32 pl-4 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:bg-white focus:border-primary transition-all text-sm cursor-pointer"
              >
                {[5, 10, 20, 50].map((v) => (
                  <option key={v} value={v}>
                    {v} per page
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Search Input */}
            <div className="relative w-full sm:w-72">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search classes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:bg-white focus:border-primary transition-all text-sm"
              />
            </div>
          </div>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="min-h-[400px] flex items-center justify-center">
            <Loading />
          </div>
        ) : classes.length > 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Class Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {classes.map((classItem) => (
                    <tr 
                      key={classItem._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{classItem.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">ID: {classItem._id.slice(-6)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          {classItem.subject?.name || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {moment(classItem.start).format("MMM DD, YYYY")}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {moment(classItem.start).format("h:mm A")} - {moment(classItem.end).format("h:mm A")}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          classItem.isLive 
                            ? "bg-green-100 text-green-700" 
                            : "bg-gray-100 text-gray-700"
                        }`}>
                          {classItem.isLive ? "Live" : "Recorded"}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {classItem.isLive && (
                            <button
                              onClick={() => setModals({ linkGenerate: classItem })}
                              className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 transition-colors"
                              title="Get Link"
                            >
                              <LinkIcon className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => navigate(routes.add_classes, {
                              state: { classItem, update: true },
                            })}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                            title="Edit"
                          >
                            <PencilSquareIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => deleteClass(classItem._id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <Pagination
                  currentPage={pageNo}
                  totalPages={totalPages}
                  onPageChange={(page) => fetchClasses(page)}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center border-2 border-dashed border-gray-100 rounded-3xl">
            <InboxIcon className="w-12 h-12 text-gray-200 mb-4" />
            <h3 className="text-gray-800 font-medium">No classes found</h3>
            <p className="text-gray-400 text-sm mt-1 max-w-xs mx-auto">
              {searchQuery
                ? `We couldn't find anything matching "${searchQuery}"`
                : "Your class list is empty. Create your first session to get started."}
            </p>
            {!searchQuery && (
              <Button
                title="Create Class"
                onClick={() => navigate(routes.add_classes)}
                className="mt-6"
              />
            )}
          </div>
        )}
      </div>

      {modals.linkGenerate && (
        <GenereteLinkModal
          onClose={() =>
            setModals((prev: any) => ({ ...prev, linkGenerate: undefined }))
          }
          classItem={modals.linkGenerate}
          isOpen={modals.linkGenerate != undefined}
        />
      )}
    </PageLayout>
  );
};

export default Classes;
